import * as Yup from "yup";
import { FORM_ERROR_MESSAGES } from "@shared/constants";
import {
  SensorDataSessionFormShape,
  CreateSensorDataSessionDto,
  UpdateSensorDataSessionDto,
} from "@containers/SensorDataSession/interfaces";
import { Hospital, SensorDataSession, Station } from "@shared/models";
import { formatDateToTz, getCurrentTimezone } from "@shared/utils";
import { actions as stationActions, selectors as stationSelectors } from "@containers/Station/store";
import { Option, USAGE_STATUS } from "@shared/interfaces";

export const FILES_UPLOAD_ACCEPT = ".csv";
const FILE_COLORS = ["#3491E3", "#FF8A00"];

export const createValidationSchemaSchema = Yup.object().shape({
  name: Yup.string().max(35, `Session Name ${FORM_ERROR_MESSAGES.LONG}`).required(FORM_ERROR_MESSAGES.REQUIRED),
  station_start_date: Yup.date()
    .test("station_start_date", FORM_ERROR_MESSAGES.REQUIRED, function (stationStartDate) {
      return !(!stationStartDate && (this.parent.station || this.parent.station_end_date));
    })
    .optional()
    .nullable(),
  station_end_date: Yup.date()
    .test("station_end_date", FORM_ERROR_MESSAGES.REQUIRED, function (stationEndDate) {
      return !(!stationEndDate && (this.parent.station || this.parent.station_start_date));
    })
    .optional()
    .nullable(),
  station_timezone: Yup.string()
    .test("station_timezone", FORM_ERROR_MESSAGES.REQUIRED, function (stationTimezone) {
      return !(
        !stationTimezone &&
        (this.parent.station || this.parent.station_start_date || this.parent.station_end_date)
      );
    })
    .optional()
    .nullable(),
  files: Yup.array()
    .test("files", FORM_ERROR_MESSAGES.REQUIRED, function (files) {
      return !(!files?.length && !this.parent.station);
    })
    .of(
      Yup.object().shape({
        custom_name: Yup.string()
          .max(35, `File Name ${FORM_ERROR_MESSAGES.LONG}`)
          .required(FORM_ERROR_MESSAGES.REQUIRED),
      }),
    ),
  hospital: Yup.number().required(FORM_ERROR_MESSAGES.REQUIRED),
  station: Yup.object()
    .shape({
      value: Yup.string().required(FORM_ERROR_MESSAGES.REQUIRED),
      label: Yup.string().required(FORM_ERROR_MESSAGES.REQUIRED),
    })
    .test("station", FORM_ERROR_MESSAGES.REQUIRED, function (station) {
      return !(!station && (this.parent.station_end_date || this.parent.station_start_date));
    })
    .optional()
    .nullable(),
});

export const getInitValues = (
  session: SensorDataSession | null | undefined,
  defaultHospitalId: number,
): SensorDataSessionFormShape => {
  if (!session) {
    return {
      name: "",
      files: [],
      hospital: defaultHospitalId,
      station: null,
      station_start_date: null,
      station_end_date: null,
      station_timezone: getCurrentTimezone(),
    };
  }

  return {
    name: session.name,
    station_start_date: session.station_start_date,
    station_end_date: session.station_end_date,
    station_timezone: session.station_timezone || getCurrentTimezone(),
    files: session.files.map((f) => ({
      id: f.id,
      original_name: f.name,
      custom_name: f.custom_name || f.name,
      key: f.id,
    })),
    station: session.station ? { label: session.station, value: session.station } : null,
    hospital: session?.hospital?.id || defaultHospitalId,
  };
};

const prepareFormData = (values: CreateSensorDataSessionDto | UpdateSensorDataSessionDto) => {
  const { files, ...data } = values;

  const formData = new FormData();
  formData.append("data", JSON.stringify(data));
  files.forEach((f) => {
    formData.append("files", f);
  });

  return formData;
};

export const prepareCreateSubmitValues = (values: SensorDataSessionFormShape) => {
  const newFiles = values.files.filter((f) => f.file);
  const existingFiles = values.files.filter((f) => f.id);

  return prepareFormData({
    name: values.name,
    files: newFiles.map((f) => f.file as File),
    new_files: newFiles.map((f) => ({ name: f.custom_name })),
    existing_files: existingFiles.map((f) => ({ id: Number(f.id), name: f.custom_name })),
    hospital_id: values.hospital,
    station: values.station ? values.station.value : null,
    station_timezone: values.station_timezone,
    station_start_date: values.station_start_date
      ? formatDateToTz(values.station_start_date, values.station_timezone)
      : null,
    station_end_date: values.station_end_date ? formatDateToTz(values.station_end_date, values.station_timezone) : null,
  });
};

export const prepareUpdateSubmitValues = (values: SensorDataSessionFormShape) => {
  const newFiles = values.files.filter((f) => f.file);
  const existingFiles = values.files.filter((f) => f.id && f.is_selected);
  const oldFiles = values.files.filter((f) => f.id && !f.is_selected);

  return prepareFormData({
    name: values.name,
    files: newFiles.map((f) => f.file as File),
    new_files: newFiles.map((f) => ({ name: f.custom_name })),
    existing_files: existingFiles.map((f) => ({ id: Number(f.id), name: f.custom_name })),
    old_files: oldFiles.map((f) => ({ id: Number(f.id), name: f.custom_name })),
    hospital_id: values.hospital ? Number(values.hospital) : null,
    station: values.station ? values.station.value : null,
    station_timezone: values.station_timezone,
    station_start_date: values.station_start_date
      ? formatDateToTz(values.station_start_date, values.station_timezone)
      : null,
    station_end_date: values.station_end_date ? formatDateToTz(values.station_end_date, values.station_timezone) : null,
  });
};

export const addColorsToItems = <T,>(items: T[]) => {
  return items.map((f, i) => ({ ...f, color: FILE_COLORS[i] || FILE_COLORS[0] }));
};

export const getInitItemsWithColors = <T,>(items: T[] | undefined, maxCount: number) => {
  const selectedItems = ((items || []) as T[]).length < maxCount ? items || [] : [];
  return addColorsToItems(selectedItems);
};

export const getHandlers = (hospital: Hospital) => {
  return {
    station: {
      getData: (search: string) =>
        stationActions.getStations.request({
          search,
          page: 0,
          limit: 100,
          usage_status: USAGE_STATUS.ALL,
          hospital: { value: String(hospital.id), label: hospital.name },
        }),
      selectData: stationSelectors.getStations,
      prepareOptionFunction: (item: Station): Option => {
        return { label: item.station_code, value: item.station_code };
      },
      loadingAction: stationActions.getStations,
    },
  };
};
